import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useRoute, useSearch } from "wouter";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArticleRenderer } from "@/components/article-renderer";
import { SecurityTrainingAcknowledge } from "@/components/security-training-acknowledge";
import { HighlightedSnippet } from "@/components/highlighted-snippet";
import { SuggestionChips } from "@/components/knowledge/suggestion-chip";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  Search,
  BookOpen,
  Rocket,
  ShieldCheck,
  Scale,
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  X,
} from "lucide-react";
import type { KnowledgeArticle, KnowledgeSection, KnowledgeSuggestion } from "@shared/schema";

// Search results carry an extra `snippet` field with `<mark>...</mark>`
// markers around the matched terms — generated server-side. The client
// `HighlightedSnippet` component splits on those markers to render bolded
// React elements (no dangerouslySetInnerHTML).
type KnowledgeSearchResult = KnowledgeArticle & { snippet?: string };

// Search responses use a `{ items, suggestions }` envelope; suggestions
// power the "Did you mean…?" chips when items is empty (e.g. typo).
type KnowledgeSearchResponse = {
  items: KnowledgeSearchResult[];
  suggestions: KnowledgeSuggestion[];
};

const SECTIONS: {
  key: KnowledgeSection;
  label: string;
  description: string;
  Icon: typeof BookOpen;
}[] = [
  {
    key: "getting-started",
    label: "Getting Started",
    description: "Onboarding walkthroughs and quick wins for new teams.",
    Icon: Rocket,
  },
  {
    key: "feature-guides",
    label: "Feature Guides",
    description: "Deep-dives into how each module of the CRM works.",
    Icon: BookOpen,
  },
  {
    key: "security-training",
    label: "Security & Training",
    description: "Mandatory training, phishing awareness, and security drills.",
    Icon: ShieldCheck,
  },
  {
    key: "compliance-policies",
    label: "Compliance & Policies",
    description: "Policies, controls, and SOC2 / SEC-relevant evidence.",
    Icon: Scale,
  },
];

function sortArticles(list: KnowledgeArticle[]): KnowledgeArticle[] {
  return [...list].sort((a, b) => {
    const oa = a.orderIndex ?? 0;
    const ob = b.orderIndex ?? 0;
    if (oa !== ob) return oa - ob;
    return a.title.localeCompare(b.title);
  });
}

/** Debounce a value — used so we don't fire a search request on every keystroke. */
function useDebouncedValue<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function KnowledgePage() {
  const [, params] = useRoute<{ slug: string }>("/knowledge/:slug");
  const slug = params?.slug;
  const [, navigate] = useLocation();

  // `useSearch` returns the query string (without the leading `?`). We re-parse
  // it on every render so the active tag updates the moment the user clicks
  // a tag chip and we navigate to /knowledge?tag=…
  const searchString = useSearch();
  const activeTag = useMemo(() => {
    const params = new URLSearchParams(searchString);
    const t = params.get("tag")?.trim();
    return t ? t : null;
  }, [searchString]);

  const { data: articles = [], isLoading } = useQuery<KnowledgeArticle[]>({
    queryKey: ["/api/knowledge"],
  });

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);
  const trimmedSearch = debouncedSearch.trim();

  // Server-side full-text search via the /api/knowledge?q= endpoint. We
  // keep the previous results visible while new ones load to avoid flicker.
  const { data: serverResponse, isFetching: isSearching } = useQuery<KnowledgeSearchResponse>({
    queryKey: ["/api/knowledge", { q: trimmedSearch }],
    queryFn: async () => {
      const res = await fetch(`/api/knowledge?q=${encodeURIComponent(trimmedSearch)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Search failed: ${res.status}`);
      return res.json();
    },
    enabled: trimmedSearch.length > 0,
    placeholderData: keepPreviousData,
  });
  const serverResults = serverResponse?.items;
  const serverSuggestions = serverResponse?.suggestions ?? [];

  // Server-side tag filter via /api/knowledge?tag=… We only fire this when a
  // tag is active so the default Knowledge Center load stays a single fetch.
  const { data: tagResults, isFetching: isTagFetching } = useQuery<KnowledgeArticle[]>({
    queryKey: ["/api/knowledge", { tag: activeTag }],
    queryFn: async () => {
      const res = await fetch(`/api/knowledge?tag=${encodeURIComponent(activeTag!)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Tag filter failed: ${res.status}`);
      return res.json();
    },
    enabled: Boolean(activeTag),
    placeholderData: keepPreviousData,
  });

  const published = useMemo(() => articles.filter((a) => a.isPublished), [articles]);

  // Determine active section: from slug or default to first section with content.
  const currentArticle = slug ? published.find((a) => a.slug === slug) : undefined;
  const [manualSection, setManualSection] = useState<KnowledgeSection | null>(null);
  const activeSection: KnowledgeSection =
    (currentArticle?.section as KnowledgeSection | undefined) ??
    manualSection ??
    SECTIONS.find((s) => published.some((a) => a.section === s.key))?.key ??
    "getting-started";

  // Reset manual section override whenever URL slug changes
  useEffect(() => {
    if (slug) setManualSection(null);
  }, [slug]);

  const sectionArticles = useMemo(
    () => sortArticles(published.filter((a) => a.section === activeSection)),
    [published, activeSection],
  );

  const searchResults = useMemo(() => {
    if (!trimmedSearch) return null;
    return (serverResults ?? []).filter((a) => a.isPublished);
  }, [trimmedSearch, serverResults]);

  // Tag filter results (only computed when tag is active and search is not).
  const taggedArticles = useMemo(() => {
    if (!activeTag) return null;
    return sortArticles((tagResults ?? []).filter((a) => a.isPublished));
  }, [activeTag, tagResults]);

  const clearTagFilter = () => {
    // Drop the ?tag= param but keep whatever pathname we're currently on so
    // the user stays on the article they're reading (or the index).
    navigate(slug ? `/knowledge/${slug}` : "/knowledge", { replace: true });
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col" data-testid="page-knowledge">
      {/* Header */}
      <div className="border-b px-4 py-4 md:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="heading-knowledge">
              Knowledge Center
            </h1>
            <p className="text-sm text-muted-foreground">
              Onboarding, feature walkthroughs, security training, and compliance policies.
            </p>
          </div>
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search articles, tags, or topics…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-9"
              data-testid="input-knowledge-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover-elevate active-elevate-2"
                data-testid="button-clear-search"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tag filter pill — shown above the layout when ?tag= is active. */}
      {activeTag && (
        <div
          className="border-b bg-muted/40 px-4 py-2 md:px-6"
          data-testid="banner-tag-filter"
        >
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">Filtered by:</span>
            <Badge variant="secondary" className="gap-1.5 pl-2 pr-1 text-xs" data-testid="badge-active-tag">
              <span className="font-medium">{activeTag}</span>
              <button
                type="button"
                onClick={clearTagFilter}
                className="ml-0.5 rounded-sm p-0.5 text-muted-foreground hover-elevate active-elevate-2"
                aria-label={`Remove tag filter ${activeTag}`}
                data-testid="button-clear-tag-filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
            {!isTagFetching && taggedArticles && (
              <span className="text-xs text-muted-foreground" data-testid="text-tag-result-count">
                {taggedArticles.length} article{taggedArticles.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
      )}

      {/* 3-pane body */}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row" data-testid="layout-knowledge-3pane">
        {/* Pane 1: sections */}
        <aside
          className="w-full shrink-0 border-b bg-muted/30 md:w-56 md:border-b-0 md:border-r"
          data-testid="pane-sections"
        >
          <ScrollArea className="h-full">
            <nav className="flex flex-row gap-1 p-2 md:flex-col">
              {SECTIONS.map((s) => {
                const SIcon = s.Icon;
                const count = published.filter((a) => a.section === s.key).length;
                const isActive = activeSection === s.key && !searchResults && !taggedArticles;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => {
                      setManualSection(s.key);
                      setSearch("");
                      if (activeTag) clearTagFilter();
                    }}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover-elevate active-elevate-2",
                      isActive ? "bg-background font-medium text-foreground" : "text-muted-foreground",
                    )}
                    data-testid={`button-section-${s.key}`}
                  >
                    <SIcon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{s.label}</span>
                    <Badge variant="outline" className="text-xs">
                      {count}
                    </Badge>
                  </button>
                );
              })}
            </nav>
          </ScrollArea>
        </aside>

        {/* Pane 2: articles in section (or search results) */}
        <aside
          className="w-full shrink-0 border-b bg-card md:w-72 md:border-b-0 md:border-r"
          data-testid="pane-articles"
        >
          <ScrollArea className="h-full">
            <div className="p-3">
              {searchResults ? (
                <>
                  <div className="mb-2 flex items-center gap-2 px-1 text-xs uppercase tracking-wide text-muted-foreground">
                    <span data-testid="text-search-results-count">
                      Search · {isSearching ? "…" : `${searchResults.length} result${searchResults.length === 1 ? "" : "s"}`}
                    </span>
                  </div>
                  {!isSearching && searchResults.length === 0 && (
                    <div className="px-1">
                      <p className="text-sm text-muted-foreground" data-testid="text-no-search-results">
                        No matches.
                      </p>
                      <SuggestionChips
                        suggestions={serverSuggestions}
                        onSelect={setSearch}
                        containerTestId="suggestions-search"
                        buttonTestIdPrefix="button-suggestion"
                        originalQuery={search}
                      />
                    </div>
                  )}
                  <ul className="flex flex-col gap-1">
                    {searchResults.map((a) => (
                      <ArticleNavItem key={a.id} article={a} activeSlug={slug} snippet={a.snippet} />
                    ))}
                  </ul>
                </>
              ) : taggedArticles ? (
                <>
                  <div className="mb-2 flex items-center gap-2 px-1 text-xs uppercase tracking-wide text-muted-foreground">
                    <span>Tag · {activeTag}</span>
                  </div>
                  {!isTagFetching && taggedArticles.length === 0 && (
                    <p className="px-1 text-sm text-muted-foreground" data-testid="text-no-tag-results">
                      No articles tagged "{activeTag}".
                    </p>
                  )}
                  <ul className="flex flex-col gap-1">
                    {taggedArticles.map((a) => (
                      <ArticleNavItem key={a.id} article={a} activeSlug={slug} />
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <div className="mb-2 px-1 text-xs uppercase tracking-wide text-muted-foreground">
                    {SECTIONS.find((s) => s.key === activeSection)?.label}
                  </div>
                  {sectionArticles.length === 0 && (
                    <p className="px-1 text-sm text-muted-foreground">No articles in this section yet.</p>
                  )}
                  <ul className="flex flex-col gap-1">
                    {sectionArticles.map((a) => (
                      <ArticleNavItem key={a.id} article={a} activeSlug={slug} />
                    ))}
                  </ul>
                </>
              )}
            </div>
          </ScrollArea>
        </aside>

        {/* Pane 3: article content (or section landing) */}
        <main className="min-w-0 flex-1 bg-background" data-testid="pane-content">
          <ScrollArea className="h-full">
            <div className="mx-auto max-w-3xl px-4 py-8 md:px-8">
              {slug ? (
                <ArticleView slug={slug} sectionArticles={sectionArticles} isLoadingAll={isLoading} />
              ) : (
                <SectionLanding
                  section={activeSection}
                  articles={sectionArticles}
                  isLoading={isLoading}
                />
              )}
            </div>
          </ScrollArea>
        </main>
      </div>
    </div>
  );
}

function ArticleNavItem({
  article,
  activeSlug,
  snippet,
}: {
  article: KnowledgeArticle;
  activeSlug: string | undefined;
  /** When provided, rendered below the title with matched terms highlighted. */
  snippet?: string;
}) {
  const isActive = article.slug === activeSlug;
  return (
    <li>
      <Link
        href={`/knowledge/${article.slug}`}
        className={cn(
          "flex flex-col gap-1 rounded-md px-2 py-1.5 text-sm transition-colors hover-elevate active-elevate-2",
          isActive ? "bg-accent font-medium text-accent-foreground" : "text-foreground/90",
        )}
        data-testid={`link-article-${article.slug}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate">{article.title}</span>
          <div className="flex shrink-0 items-center gap-1">
            {article.isDraft && (
              <Badge
                variant="outline"
                className="border-amber-400 text-[10px] text-amber-700 dark:text-amber-400"
              >
                DRAFT
              </Badge>
            )}
            {article.isPublic && (
              <Badge variant="secondary" className="text-[10px]">
                Public
              </Badge>
            )}
          </div>
        </div>
        {snippet && (
          <HighlightedSnippet
            snippet={snippet}
            className="line-clamp-2"
            testId={`snippet-article-${article.slug}`}
          />
        )}
      </Link>
    </li>
  );
}

function SectionLanding({
  section,
  articles,
  isLoading,
}: {
  section: KnowledgeSection;
  articles: KnowledgeArticle[];
  isLoading: boolean;
}) {
  const meta = SECTIONS.find((s) => s.key === section)!;
  const Icon = meta.Icon;
  return (
    <div data-testid={`section-${section}`}>
      <div className="mb-5 flex items-start gap-3">
        <div className="rounded-md border bg-card p-2">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold">{meta.label}</h2>
          <p className="text-sm text-muted-foreground">{meta.description}</p>
        </div>
      </div>
      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-5 w-2/3 rounded bg-muted" />
                <div className="h-3 w-full rounded bg-muted" />
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No articles in this section yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {articles.map((a) => (
            <Link
              key={a.id}
              href={`/knowledge/${a.slug}`}
              className="block"
              data-testid={`card-article-${a.slug}`}
            >
              <Card className="h-full transition-colors hover-elevate active-elevate-2">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base leading-tight">{a.title}</CardTitle>
                    <div className="flex flex-col items-end gap-1">
                      {a.isDraft && (
                        <Badge
                          variant="outline"
                          className="border-amber-400 text-amber-700 dark:text-amber-400"
                        >
                          DRAFT
                        </Badge>
                      )}
                      {a.isPublic && <Badge variant="secondary">Public</Badge>}
                    </div>
                  </div>
                  {a.summary && (
                    <CardDescription className="line-clamp-2">{a.summary}</CardDescription>
                  )}
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleView({
  slug,
  sectionArticles,
  isLoadingAll,
}: {
  slug: string;
  sectionArticles: KnowledgeArticle[];
  isLoadingAll: boolean;
}) {
  const [, navigate] = useLocation();
  const { data: article, isLoading, error } = useQuery<KnowledgeArticle>({
    queryKey: ["/api/knowledge", slug],
  });

  // Related articles — same section + shared tags. Computed server-side so
  // it doesn't depend on the in-memory list of all articles for the user.
  const { data: related = [] } = useQuery<KnowledgeArticle[]>({
    queryKey: ["/api/knowledge", slug, "related"],
    enabled: Boolean(article),
  });

  const { prev, next } = useMemo(() => {
    if (!article) return { prev: undefined, next: undefined };
    const idx = sectionArticles.findIndex((a) => a.id === article.id);
    return {
      prev: idx > 0 ? sectionArticles[idx - 1] : undefined,
      next: idx >= 0 && idx < sectionArticles.length - 1 ? sectionArticles[idx + 1] : undefined,
    };
  }, [article, sectionArticles]);

  if (isLoading || isLoadingAll) {
    return (
      <div>
        <div className="h-8 w-1/2 animate-pulse rounded bg-muted" />
        <div className="mt-3 h-4 w-3/4 animate-pulse rounded bg-muted" />
        <div className="mt-6 space-y-2">
          <div className="h-3 w-full animate-pulse rounded bg-muted" />
          <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">This article could not be loaded.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/knowledge")}
          data-testid="button-back-to-knowledge"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Knowledge Center
        </Button>
      </div>
    );
  }

  const sectionMeta = SECTIONS.find((s) => s.key === article.section);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {sectionMeta && <span>{sectionMeta.label}</span>}
        {article.tags?.map((t) => (
          <Link
            key={t}
            href={`/knowledge?tag=${encodeURIComponent(t)}`}
            data-testid={`link-article-tag-${t}`}
          >
            <Badge
              variant="outline"
              className="cursor-pointer text-xs transition-colors hover-elevate active-elevate-2"
              title={`Browse articles tagged "${t}"`}
            >
              {t}
            </Badge>
          </Link>
        ))}
      </div>

      <h1 className="text-3xl font-bold tracking-tight" data-testid="text-article-title">
        {article.title}
      </h1>
      {article.summary && (
        <p className="mt-2 text-lg text-muted-foreground">{article.summary}</p>
      )}

      {article.isDraft && (
        <div
          className="my-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30"
          data-testid="banner-draft"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400" />
          <div className="text-sm">
            <div className="font-semibold text-amber-900 dark:text-amber-200">
              DRAFT — pending legal & compliance review
            </div>
            <div className="text-amber-900/90 dark:text-amber-200/90">
              This document is published internally for review only and is not yet binding. Treat as
              a working draft.
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <ArticleRenderer markdown={article.bodyMarkdown} videoUrl={article.videoUrl} />
      </div>

      {/* Self-hides when the server returns mandatory:false for this slug/role. */}
      <SecurityTrainingAcknowledge slug={article.slug} />


      {(prev || next) && (
        <div
          className="mt-10 grid gap-3 border-t pt-6 sm:grid-cols-2"
          data-testid="nav-article-pagination"
        >
          {prev ? (
            <Link href={`/knowledge/${prev.slug}`} data-testid={`link-prev-article-${prev.slug}`}>
              <Card className="h-full transition-colors hover-elevate active-elevate-2">
                <CardHeader>
                  <CardDescription className="flex items-center gap-1 text-xs">
                    <ArrowLeft className="h-3 w-3" /> Previous
                  </CardDescription>
                  <CardTitle className="text-sm leading-tight">{prev.title}</CardTitle>
                </CardHeader>
              </Card>
            </Link>
          ) : (
            <div />
          )}
          {next ? (
            <Link href={`/knowledge/${next.slug}`} data-testid={`link-next-article-${next.slug}`}>
              <Card className="h-full transition-colors hover-elevate active-elevate-2 sm:text-right">
                <CardHeader>
                  <CardDescription className="flex items-center gap-1 text-xs sm:justify-end">
                    Next <ArrowRight className="h-3 w-3" />
                  </CardDescription>
                  <CardTitle className="text-sm leading-tight">{next.title}</CardTitle>
                </CardHeader>
              </Card>
            </Link>
          ) : (
            <div />
          )}
        </div>
      )}

      {related.length > 0 && (
        <div className="mt-10 border-t pt-6" data-testid="related-articles">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Related articles
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {related.map((r) => (
              <Link
                key={r.id}
                href={`/knowledge/${r.slug}`}
                className="block"
                data-testid={`link-related-${r.slug}`}
              >
                <Card className="h-full transition-colors hover-elevate active-elevate-2">
                  <CardHeader>
                    <CardTitle className="text-sm leading-tight">{r.title}</CardTitle>
                    {r.summary && (
                      <CardDescription className="line-clamp-2 text-xs">
                        {r.summary}
                      </CardDescription>
                    )}
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
